import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';
import {
  LEGAL_PAGE_TITLE,
  LEGAL_PAGE_DESCRIPTION,
} from '@/shared/constants/seoConstants';
import { ROOT_POLICIES_PAGE } from '@/shared/constants/urlConstants';
import { typeguardForString } from '@/shared/utils/typeguardForString';
import {
  POLICY_DOCUMENTS,
  OFFSET_FOR_SCROLL,
  OFFSET_FOR_SCROLL_MOBILE,
} from '@/pages/policies/lib/policiesDocuments';
import type { PolicyDocumentMeta } from '@/pages/policies/model/types/policies';

export const usePolicies = () => {
  const route = useRoute();
  const isMobile: boolean = useIsMobile();
  const scrollY: Ref<number> = ref(0);

  const slug = computed(() => typeguardForString(route.params.slug) ?? '');

  const activeDocument = computed<PolicyDocumentMeta | null>(
    () => POLICY_DOCUMENTS.find((document) => document.slug === slug.value) ?? null,
  );

  if (slug.value && !activeDocument.value) {
    throw createError({ statusCode: 404, statusMessage: 'Документ не найден' });
  }

  const metaTitle = computed(() => activeDocument.value?.title ?? LEGAL_PAGE_TITLE);

  const metaDescription = computed(
    () => activeDocument.value?.description ?? LEGAL_PAGE_DESCRIPTION,
  );

  const breadcrumbs = computed<Breadcrumb[]>(() => {
    if (!activeDocument.value) {
      return [
        { id: 1, text: 'Главная', to: '/' },
        { id: 2, text: 'Правовые документы' },
      ];
    }

    return [
      { id: 1, text: 'Главная', to: '/' },
      { id: 2, text: 'Правовые документы', to: ROOT_POLICIES_PAGE },
      { id: 3, text: activeDocument.value.label },
    ];
  });

  const scrollOffset: ComputedRef<number> = computed(() =>
    isMobile ? OFFSET_FOR_SCROLL_MOBILE : OFFSET_FOR_SCROLL,
  );

  const isShowScrollButton = computed(() => scrollY.value > scrollOffset.value);

  const handleScroll = (): void => {
    scrollY.value = window.scrollY;
  };

  onMounted(() => {
    window.addEventListener('scroll', handleScroll);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
  });

  return {
    slug,
    activeDocument,
    metaTitle,
    metaDescription,
    breadcrumbs,
    isShowScrollButton,
  };
};
